import { ComponentProps } from "react";
import { useWindowSize } from "@hooks/useWindowSize";
import Card from "@particles/card/Card";
import { CardVariations } from "@particles/card/utils/types";
import { ImagePresets } from "@routes/Article/components/ArticleThumbnail/utils/enums";
import { cn } from "@util/helpers";

type PodcastsFeaturedStoriesProps = {
  /** The list of podcast featured stories */
  cardProps: ComponentProps<typeof Card>[];
  /** Additional class names for the component */
  className?: string;
};

/**
 * Component to display featured podcast stories in a grid layout
 * Figma: https://www.figma.com/design/xsbEGttNPBTNUi388qtSJD/PODCASTS--WEB-?node-id=3702-295143&t=Rc3p8bM01UozhUND-4
 */
const PodcastsFeaturedStories = ({
  cardProps,
  className,
}: PodcastsFeaturedStoriesProps): React.ReactElement => {
  const { isScreenLG } = useWindowSize();

  const topStoriesVariant: CardVariations = {
    image: {
      width: "w-full lg:w-1/2",
      position: "left",
      aspectRatio: "aspect-3x2",
      orientation: "landscape",
      filters: { w: ImagePresets.MEDIUM, h: 600, dpr: 1, f: "webp" },
    },
    content: {
      width: "w-full lg:w-1/2",
      extraClass: "lg:pl-4 pt-4 lg:pt-[0.125rem]",
      blurb: {
        extraClass:
          "text-gray-650 font-lucida text-xs -tracking-5% font-medium max-lg:[display:-webkit-box] line-clamp-3",
      },
    },
    subscriberLabel: { extraClass: "mt-2 mb-0" },
    title: {
      size: "text-4xl",
      color: "text-gray-850",
      extraClass: cn("font-bold font-lct mb-1 -tracking-2%"),
    },
    partnership: {
      extraClass: cn(
        "font-poppins text-4xs text-gray-175 leading-1 font-light border border-gray-175 px-2 py-1 w-fit mb-1"
      ),
      messageClassName: "text-gray-575",
      nameClassName: "text-gray-650",
    },
  };

  const storiesVariant: CardVariations = {
    image: {
      width: "w-[100px]",
      position: "left",
      aspectRatio: "aspect-square",
      orientation: "square",
      filters: { w: ImagePresets.MEDIUM, h: 600, dpr: 1, f: "webp" },
    },
    content: {
      width: "w-3/4",
      extraClass: "pl-3 pt-[0.125rem]",
      blurb: {
        extraClass: cn(
          "text-gray-850 font-lucida text-xs -tracking-5% font-medium"
        ),
      },
    },
    subscriberLabel: { extraClass: "mt-2 mb-0" },
    title: {
      size: "text-base",
      color: "text-gray-850",
      extraClass: cn("font-bold font-lct -tracking-2%"),
    },
    partnership: {
      extraClass: cn(
        "font-poppins text-4xs text-gray-175 leading-1 font-light border border-gray-175 px-2 py-1 w-fit mb-1"
      ),
      messageClassName: "text-gray-575",
      nameClassName: "text-gray-650",
    },
  };

  return (
    <div
      className={cn("grid lg:grid-cols-2", className)}
      data-testid="podcasts-featured-stories-component"
    >
      {cardProps.map((cardProp, index) => (
        <div
          key={cardProp.id}
          className={cn("relative col-span-2 border-b border-gray-175 py-4", {
            "col-span-2 lg:col-span-1": index !== 0,
            "col-span-2": index === 0,
            "border-0": index === cardProps.length - 1,
          })}
        >
          <Card
            key={cardProp.id}
            {...cardProp}
            className={cn({
              "ml-4 before:absolute before:-left-4 before:h-full before:w-[1px] before:bg-gray-175 ":
                index % 2 === 0 && index !== 0 && isScreenLG,
              "mr-4": index % 2 !== 0,
            })}
            variations={index === 0 ? topStoriesVariant : storiesVariant}
          />
        </div>
      ))}
    </div>
  );
};

export default PodcastsFeaturedStories;
